import useUiNotification from '~/composables/useUiNotification';
import type { PaymentMethod } from '~/composables/custom/usePayment/usePayment';

interface UnzerResponse {
  id?: string;
  isSuccess: boolean;
  isPending: boolean;
  isResumed: boolean;
  isError: boolean;
  url: string;
  timestamp: Date;
  errors: {
    code: string;
    merchantMessage: string;
    customerMessage: string;
  }[];
}

export const useUnzer = () => {
  const errors = ref({ token: '' });
  const { $config, $domain } = useNuxtApp();
  const { send } = useUiNotification();

  const initializeUnzerPayment = async (paymentOption: Pick<PaymentMethod, 'method' | 'provider'>) => {
    errors.value.token = '';

    const { unzer } = $config.public.payment;

    const { unzerClientId: clientId } = $domain;
    const unzerPaymentTypeUrl = unzer[paymentOption?.method];

    try {
      if (!unzerPaymentTypeUrl || !clientId) {
        throw new Error('Not possible to get payment types since mandatory configurations are missing');
      }

      const token = btoa(`${clientId}:`);

      if (!token) {
        throw new Error('Error getting token for Unzer.');
      }

      const data = await fetch(unzerPaymentTypeUrl, {
        cache: 'no-cache',
        referrerPolicy: 'no-referrer',
        method: 'POST',
        headers: {
          'Authorization': `Basic ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result: UnzerResponse = await data.json();

      if (result?.isError) {
        throw new Error(result.errors.map(error => `${error.code}: ${error.customerMessage}`).join('. '));
      }

      if (!result?.id) {
        throw new Error('Error getting resource.id from Unzer');
      }

      return result?.id;
    }
    catch (error) {
      if (error instanceof Error) {
        throw new Error(`initializeUnzerPayment :: ${error.message}`, {
          clientId, paymentOption: { provider: paymentOption.provider, method: paymentOption.method }, unzerPaymentTypeUrl,
        });
      }
      errors.value.token = `Error getting unzer key for ${paymentOption?.method}`;
      send({
        message: 'somethingWentWrong',
        type: 'danger',
      });
    }
    return undefined;
  };

  return {
    initializeUnzerPayment,
    errors: computed(() => errors.value),
  };
};
